// mirror of auware/.../frontend/src/components/ui/Card.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type HTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Padding presets for the card. */
type CardPadding = "none" | "sm" | "md" | "lg";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding preset. Defaults to `"md"`. */
  padding?: CardPadding;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const baseClasses = "rounded-lg border border-neutral-200 bg-white shadow-sm";

const paddingClasses: Record<CardPadding, string> = {
  none: "p-0",
  sm: "p-4",
  md: "p-6",
  lg: "p-8",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Content card container with configurable padding.
 *
 * ```tsx
 * <Card padding="lg">
 *   <h2>Title</h2>
 *   <p>Body content here.</p>
 * </Card>
 * ```
 */
const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className = "", padding = "md", children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`${baseClasses} ${paddingClasses[padding]} ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = "Card";

export default Card;
